import { useEffect, useState } from "react";

interface Message {
  from: "caller" | "ai";
  text: string;
  delay: number;
}

const conversation: Message[] = [
  { from: "caller", text: "Hola, quería pedir cita para una revisión dental el próximo lunes...", delay: 800 },
  { from: "ai", text: "¡Hola! Claro, consulto disponibilidad ahora mismo. ¿Tienes preferencia de horario, mañana o tarde?", delay: 2200 },
  { from: "caller", text: "Preferiblemente por la mañana, entre las 10 y las 12.", delay: 4000 },
  { from: "ai", text: "Perfecto. Tengo hueco el lunes 23 a las 10:30 h con el Dr. Martínez. ¿Te viene bien?", delay: 5800 },
  { from: "caller", text: "Sí, ese me va genial.", delay: 7200 },
  { from: "ai", text: "Reservado. Te envío confirmación por SMS. ¿Necesitas algo más?", delay: 8800 },
];

export default function LiveDemoAnimation({ active }: { active: boolean }) {
  const [visibleMessages, setVisibleMessages] = useState<number>(0);
  const [typing, setTyping] = useState(false);
  const [callStatus, setCallStatus] = useState<"idle" | "ringing" | "connected" | "done">("idle");
  const [elapsed, setElapsed] = useState(0);

  useEffect(() => {
    if (!active) {
      setVisibleMessages(0);
      setTyping(false);
      setCallStatus("idle");
      setElapsed(0);
      return;
    }

    const t0 = setTimeout(() => setCallStatus("ringing"), 200);
    const t1 = setTimeout(() => setCallStatus("connected"), 1200);

    const timers: ReturnType<typeof setTimeout>[] = [t0, t1];

    conversation.forEach((msg, i) => {
      const showTyping = setTimeout(() => setTyping(true), msg.delay - 500);
      const showMsg = setTimeout(() => {
        setTyping(false);
        setVisibleMessages(i + 1);
      }, msg.delay);
      timers.push(showTyping, showMsg);
    });

    const done = setTimeout(() => setCallStatus("done"), 10200);
    timers.push(done);

    const ticker = setInterval(() => setElapsed((p) => p + 1), 1000);

    return () => {
      timers.forEach(clearTimeout);
      clearInterval(ticker);
    };
  }, [active]);

  const formatTime = (s: number) => `${String(Math.floor(s / 60)).padStart(2, "0")}:${String(s % 60).padStart(2, "0")}`;

  return (
    <div className="bg-white border border-stone-100 rounded-3xl overflow-hidden">
      {/* Chrome bar */}
      <div className="bg-stone-900 px-6 py-4 flex items-center justify-between">
        <div className="flex items-center gap-3">
          <img
            src="https://static.readdy.ai/image/464d6b2d88c2c0b329c43db37f7a6b21/5e3d82f418168fbdbc49efd95d2ee821.png"
            alt="VoiceAI"
            className="h-6 w-auto"
          />
          <span className="text-white/60 text-xs">Demo en vivo · Clínica Ejemplo</span>
        </div>
        <div className="flex items-center gap-1.5">
          <span className="w-2.5 h-2.5 rounded-full bg-red-400" />
          <span className="w-2.5 h-2.5 rounded-full bg-amber-400" />
          <span className="w-2.5 h-2.5 rounded-full bg-emerald-400" />
        </div>
      </div>

      {/* Call status bar */}
      <div className="px-5 pt-4 pb-2">
        <div
          className={`rounded-2xl px-4 py-3 flex items-center justify-between transition-all duration-500 ${
            callStatus === "idle"
              ? "bg-stone-50"
              : callStatus === "ringing"
              ? "bg-amber-50 border border-amber-100"
              : callStatus === "connected"
              ? "bg-emerald-50 border border-emerald-100"
              : "bg-stone-50 border border-stone-100"
          }`}
        >
          <div className="flex items-center gap-3">
            <div
              className={`w-8 h-8 flex items-center justify-center rounded-full flex-shrink-0 transition-colors duration-300 ${
                callStatus === "idle"
                  ? "bg-stone-200"
                  : callStatus === "ringing"
                  ? "bg-amber-400"
                  : callStatus === "connected"
                  ? "bg-emerald-400"
                  : "bg-stone-300"
              }`}
            >
              <i
                className={`text-white text-sm ${
                  callStatus === "ringing"
                    ? "ri-phone-line animate-bounce"
                    : callStatus === "connected"
                    ? "ri-phone-fill"
                    : callStatus === "done"
                    ? "ri-check-line"
                    : "ri-phone-line"
                }`}
              />
            </div>
            <div>
              <p className="text-stone-800 text-xs font-semibold">
                {callStatus === "idle" && "Esperando llamada..."}
                {callStatus === "ringing" && "Llamada entrante"}
                {callStatus === "connected" && "En llamada · IA activa"}
                {callStatus === "done" && "Llamada completada"}
              </p>
              <p className="text-stone-400 text-xs">
                {callStatus === "ringing" && "+34 612 345 678"}
                {callStatus === "connected" && `Duración: ${formatTime(elapsed)}`}
                {callStatus === "done" && "Cita reservada · SMS enviado"}
                {callStatus === "idle" && "Recepcionista IA lista"}
              </p>
            </div>
          </div>

          {callStatus === "connected" && (
            <div className="flex items-end gap-0.5 h-5">
              {[3, 5, 7, 4, 6, 8, 5].map((h, i) => (
                <div
                  key={i}
                  className="w-1 rounded-full bg-emerald-400"
                  style={{
                    height: `${h * 2}px`,
                    animation: `waveBar 0.8s ease-in-out infinite`,
                    animationDelay: `${i * 0.1}s`,
                  }}
                />
              ))}
            </div>
          )}

          {callStatus === "done" && (
            <span className="text-xs font-semibold text-emerald-600 bg-emerald-50 border border-emerald-100 px-2 py-0.5 rounded-full">
              Resuelto
            </span>
          )}
        </div>
      </div>

      {/* Chat bubbles */}
      <div className="px-5 pb-5 space-y-3 min-h-64 max-h-72 overflow-y-auto">
        {conversation.slice(0, visibleMessages).map((msg, i) => (
          <div
            key={i}
            className={`flex items-end gap-2 ${msg.from === "caller" ? "justify-start" : "justify-end"}`}
            style={{ animation: "fadeSlideUp 0.35s ease-out forwards" }}
          >
            {msg.from === "caller" && (
              <div className="w-6 h-6 flex items-center justify-center rounded-full bg-stone-200 flex-shrink-0">
                <i className="ri-user-line text-stone-500 text-xs" />
              </div>
            )}
            <div
              className={`max-w-xs px-4 py-2.5 rounded-2xl text-xs leading-relaxed ${
                msg.from === "caller"
                  ? "bg-stone-100 text-stone-700 rounded-bl-sm"
                  : "bg-amber-500 text-white rounded-br-sm"
              }`}
            >
              {msg.text}
            </div>
            {msg.from === "ai" && (
              <div className="w-6 h-6 flex items-center justify-center rounded-full bg-amber-500 flex-shrink-0">
                <i className="ri-robot-line text-white text-xs" />
              </div>
            )}
          </div>
        ))}

        {typing && (
          <div className="flex items-end gap-2 justify-end">
            <div className="px-4 py-3 bg-amber-100 rounded-2xl rounded-br-sm flex items-center gap-1">
              <span className="w-1.5 h-1.5 rounded-full bg-amber-500 animate-bounce" style={{ animationDelay: "0ms" }} />
              <span className="w-1.5 h-1.5 rounded-full bg-amber-500 animate-bounce" style={{ animationDelay: "150ms" }} />
              <span className="w-1.5 h-1.5 rounded-full bg-amber-500 animate-bounce" style={{ animationDelay: "300ms" }} />
            </div>
            <div className="w-6 h-6 flex items-center justify-center rounded-full bg-amber-500 flex-shrink-0">
              <i className="ri-robot-line text-white text-xs" />
            </div>
          </div>
        )}

        {callStatus === "done" && visibleMessages === conversation.length && (
          <div
            className="mt-2 bg-stone-50 rounded-2xl px-4 py-3 border border-stone-100"
            style={{ animation: "fadeSlideUp 0.4s ease-out forwards" }}
          >
            <p className="text-xs font-semibold text-stone-700 mb-1.5">Resumen de la llamada</p>
            <div className="space-y-1">
              {[
                { icon: "ri-calendar-check-line", text: "Cita: Lunes 23, 10:30 h · Dr. Martínez" },
                { icon: "ri-message-3-line", text: "SMS de confirmación enviado" },
                { icon: "ri-time-line", text: "Duración: 00:42 · Resultado: Reserva exitosa" },
              ].map((item) => (
                <div key={item.text} className="flex items-center gap-2">
                  <div className="w-4 h-4 flex items-center justify-center">
                    <i className={`${item.icon} text-amber-500 text-xs`} />
                  </div>
                  <span className="text-stone-500 text-xs">{item.text}</span>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>

      <style>{`
        @keyframes fadeSlideUp {
          from { opacity: 0; transform: translateY(8px); }
          to { opacity: 1; transform: translateY(0); }
        }
        @keyframes waveBar {
          0%, 100% { transform: scaleY(0.5); }
          50% { transform: scaleY(1); }
        }
      `}</style>
    </div>
  );
}
